import { Button } from '@/components/ui/button'
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList
} from '@/components/ui/command'
import { Input } from '@/components/ui/input'
import {
  Popover,
  PopoverContent,
  PopoverTrigger
} from '@/components/ui/popover'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '@/components/ui/select'
import { Switch } from '@/components/ui/switch'
import { Textarea } from '@/components/ui/textarea'
import { useDebounce } from '@/hooks/useDebounce'
import { lookup } from '@/lib/resources'
import type { ResourceField } from '@/lib/resources'
import { cn } from '@/lib/utils'
import { useQuery } from '@tanstack/react-query'
import { Check, ChevronsUpDown } from 'lucide-react'
import { useMemo, useState } from 'react'

interface Props {
  field: ResourceField
  value: unknown
  onChange: (value: unknown) => void
  disabled?: boolean
}

export function ResourceFieldEditor({
  field,
  value,
  onChange,
  disabled
}: Props) {
  if (field.readonly || field.widget === 'readonly') {
    return (
      <Input
        value={value === null || value === undefined ? '' : String(value)}
        readOnly
        disabled
      />
    )
  }

  if (field.choices && field.choices.length > 0) {
    return (
      <Select
        value={value === null || value === undefined ? '' : String(value)}
        onValueChange={(v) => onChange(coerceChoice(v, field))}
        disabled={disabled}
      >
        <SelectTrigger>
          <SelectValue placeholder={`Select ${field.label.toLowerCase()}…`} />
        </SelectTrigger>
        <SelectContent>
          {field.choices.map((c) => (
            <SelectItem key={String(c.value)} value={String(c.value)}>
              {c.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    )
  }

  switch (field.widget) {
    case 'switch':
      return (
        <Switch
          checked={Boolean(value)}
          onCheckedChange={onChange}
          disabled={disabled}
        />
      )
    case 'textarea':
      return (
        <Textarea
          value={(value as string) ?? ''}
          onChange={(e) => onChange(e.target.value)}
          rows={4}
          disabled={disabled}
        />
      )
    case 'json':
      return (
        <Textarea
          value={
            value === null || value === undefined
              ? ''
              : JSON.stringify(value, null, 2)
          }
          onChange={(e) => {
            const text = e.target.value
            try {
              onChange(text === '' ? null : JSON.parse(text))
            } catch {
              onChange(text) // leave as raw — server will reject if invalid
            }
          }}
          rows={6}
          className="font-mono text-xs"
          disabled={disabled}
        />
      )
    case 'number':
      return (
        <Input
          type="number"
          value={value === null || value === undefined ? '' : String(value)}
          onChange={(e) =>
            onChange(e.target.value === '' ? null : Number(e.target.value))
          }
          disabled={disabled}
        />
      )
    case 'date':
      return (
        <Input
          type="date"
          value={typeof value === 'string' ? value.slice(0, 10) : ''}
          onChange={(e) => onChange(e.target.value || null)}
          disabled={disabled}
        />
      )
    case 'datetime':
      return (
        <Input
          type="datetime-local"
          value={typeof value === 'string' ? value.slice(0, 16) : ''}
          onChange={(e) => onChange(e.target.value || null)}
          disabled={disabled}
        />
      )
    case 'email':
      return (
        <Input
          type="email"
          value={(value as string) ?? ''}
          onChange={(e) => onChange(e.target.value)}
          disabled={disabled}
        />
      )
    case 'url':
      return (
        <Input
          type="url"
          value={(value as string) ?? ''}
          onChange={(e) => onChange(e.target.value)}
          disabled={disabled}
        />
      )
    case 'fk':
      return (
        <FkCombobox
          field={field}
          value={value}
          onChange={onChange}
          disabled={disabled}
        />
      )
    case 'fk-multi':
      return (
        <FkMultiSelect
          field={field}
          value={value}
          onChange={onChange}
          disabled={disabled}
        />
      )
    default:
      return (
        <Input
          value={(value as string) ?? ''}
          onChange={(e) => onChange(e.target.value)}
          disabled={disabled}
        />
      )
  }
}

function coerceChoice(value: string, field: ResourceField): unknown {
  if (!field.choices) return value
  const match = field.choices.find((c) => String(c.value) === value)
  return match ? match.value : value
}

interface FkProps {
  field: ResourceField
  value: unknown
  onChange: (v: unknown) => void
  disabled?: boolean
}

function FkCombobox({ field, value, onChange, disabled }: FkProps) {
  const [open, setOpen] = useState(false)
  const [search, setSearch] = useState('')
  const debounced = useDebounce(search, 200)
  const resourceKey = field.fk_resource

  const { data, isFetching } = useQuery({
    queryKey: ['fk-lookup', resourceKey, debounced],
    queryFn: () => lookup(resourceKey!, debounced),
    enabled: Boolean(resourceKey) && open,
    staleTime: 30_000
  })

  const items = data?.results ?? []
  const selectedLabel = useMemo(() => {
    if (value === null || value === undefined) return null
    const match = items.find((i) => String(i.id) === String(value))
    return match?.label ?? null
  }, [items, value])

  if (!resourceKey) {
    return (
      <Input
        value={value === null || value === undefined ? '' : String(value)}
        onChange={(e) =>
          onChange(
            e.target.value === ''
              ? null
              : Number(e.target.value) || e.target.value
          )
        }
        disabled={disabled}
        placeholder="ID"
      />
    )
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          role="combobox"
          disabled={disabled}
          className={cn(
            'w-full justify-between font-normal',
            value === null || value === undefined ? 'text-muted-foreground' : ''
          )}
        >
          {selectedLabel ??
            (value !== null && value !== undefined
              ? `#${String(value)}`
              : `Pick a ${field.label.toLowerCase()}…`)}
          <ChevronsUpDown className="size-4 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent
        className="w-[var(--radix-popover-trigger-width)] p-0"
        align="start"
      >
        <Command shouldFilter={false}>
          <CommandInput
            placeholder="Search…"
            value={search}
            onValueChange={setSearch}
          />
          <CommandList>
            <CommandEmpty>
              {isFetching ? 'Loading…' : 'No matches.'}
            </CommandEmpty>
            <CommandGroup>
              <CommandItem
                value="__clear"
                onSelect={() => {
                  onChange(null)
                  setOpen(false)
                }}
              >
                <span className="text-muted-foreground">Clear</span>
              </CommandItem>
              {items.map((item) => (
                <CommandItem
                  key={String(item.id)}
                  value={String(item.id)}
                  onSelect={() => {
                    onChange(item.id)
                    setOpen(false)
                  }}
                >
                  <Check
                    className={cn(
                      'mr-2 size-4',
                      String(value) === String(item.id)
                        ? 'opacity-100'
                        : 'opacity-0'
                    )}
                  />
                  {item.label}
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  )
}

function FkMultiSelect({ field, value, onChange }: FkProps) {
  // Deferred to a later phase — for Phase 1 just show a JSON textarea.
  return (
    <Textarea
      value={JSON.stringify(value ?? [], null, 2)}
      onChange={(e) => {
        try {
          onChange(JSON.parse(e.target.value))
        } catch {
          onChange(e.target.value)
        }
      }}
      rows={3}
      className="font-mono text-xs"
      placeholder={`["${field.fk_model}_id_1", "${field.fk_model}_id_2"]`}
    />
  )
}
